/**
 * Realtime sync.
 *
 * The server broadcasts `{type:"workspaceChanged", reason}` whenever the scraper
 * imports, or either client writes. The message carries no data — it is a nudge
 * to refetch the snapshot, which keeps the socket protocol trivial and means a
 * missed message costs a stale view, not a wrong one.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { config, realtimeEnabled } from '../config';

export type RealtimeStatus = 'disabled' | 'connecting' | 'live' | 'offline' | 'error';

/** Several writes in a row (a bulk import) should cause one refetch, not twenty. */
const REFRESH_DEBOUNCE_MS = 600;
const PING_INTERVAL_MS = 30_000;
const MAX_RECONNECT_ATTEMPTS = 6;
const BASE_RECONNECT_DELAY_MS = 1000;

interface Options {
  getAccessToken: () => Promise<string | null>;
  onChanged: () => void;
  /** Presence pings only — do not refetch the full snapshot. */
  onPresenceChanged?: () => void;
  enabled: boolean;
}

export function useRealtime({
  getAccessToken,
  onChanged,
  onPresenceChanged,
  enabled,
}: Options): RealtimeStatus {
  const [status, setStatus] = useState<RealtimeStatus>(realtimeEnabled ? 'connecting' : 'disabled');

  // Kept in refs so reconnect logic never re-runs the effect and drops the socket.
  const socketRef = useRef<WebSocket | null>(null);
  const attemptsRef = useRef(0);
  const debounceRef = useRef<number | undefined>(undefined);
  const presenceDebounceRef = useRef<number | undefined>(undefined);
  const pingRef = useRef<number | undefined>(undefined);
  const reconnectRef = useRef<number | undefined>(undefined);
  const closedByUsRef = useRef(false);
  const onChangedRef = useRef(onChanged);
  const onPresenceChangedRef = useRef(onPresenceChanged);

  onChangedRef.current = onChanged;
  onPresenceChangedRef.current = onPresenceChanged;

  const scheduleRefresh = useCallback(() => {
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => onChangedRef.current(), REFRESH_DEBOUNCE_MS);
  }, []);

  const schedulePresenceRefresh = useCallback(() => {
    window.clearTimeout(presenceDebounceRef.current);
    presenceDebounceRef.current = window.setTimeout(() => onPresenceChangedRef.current?.(), 400);
  }, []);

  useEffect(() => {
    if (!realtimeEnabled || !enabled) {
      setStatus(realtimeEnabled ? 'offline' : 'disabled');
      return;
    }

    closedByUsRef.current = false;
    let disposed = false;

    const cleanupTimers = () => {
      window.clearInterval(pingRef.current);
      window.clearTimeout(reconnectRef.current);
    };

    const connect = async () => {
      if (disposed) return;

      // A browser cannot set headers on a WebSocket handshake, so the access
      // token rides in the query string. It is short-lived and the connection is
      // wss, but it does mean the token reaches CloudWatch access logs.
      const token = await getAccessToken();
      if (disposed) return;
      if (!token) {
        setStatus('offline');
        return;
      }

      setStatus('connecting');

      const socket = new WebSocket(`${config.webSocketEndpoint}?token=${encodeURIComponent(token)}`);
      socketRef.current = socket;

      socket.onopen = () => {
        if (disposed) return;
        attemptsRef.current = 0;
        setStatus('live');
        socket.send(JSON.stringify({ action: 'subscribe' }));
        window.clearInterval(pingRef.current);
        pingRef.current = window.setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ action: 'ping' }));
          }
        }, PING_INTERVAL_MS);
      };

      socket.onmessage = (event) => {
        let message: { type?: string };
        try {
          message = JSON.parse(String(event.data));
        } catch {
          return;
        }
        if (message.type === 'workspaceChanged') scheduleRefresh();
        if (message.type === 'presenceChanged') schedulePresenceRefresh();
      };

      socket.onerror = () => {
        // `onclose` always follows, and that is where reconnect is handled.
        if (!disposed && attemptsRef.current >= MAX_RECONNECT_ATTEMPTS) setStatus('error');
      };

      socket.onclose = () => {
        window.clearInterval(pingRef.current);
        if (disposed || closedByUsRef.current) return;

        if (attemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          setStatus('error');
          return;
        }
        const delay = BASE_RECONNECT_DELAY_MS * 2 ** attemptsRef.current;
        attemptsRef.current += 1;
        setStatus('connecting');
        reconnectRef.current = window.setTimeout(connect, delay);
      };
    };

    void connect();

    // Coming back to a backgrounded tab: refetch immediately, because API
    // Gateway idles sockets out after 10 minutes and messages were missed.
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      scheduleRefresh();
      if (socketRef.current?.readyState !== WebSocket.OPEN) {
        attemptsRef.current = 0;
        void connect();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      disposed = true;
      closedByUsRef.current = true;
      document.removeEventListener('visibilitychange', onVisible);
      cleanupTimers();
      window.clearTimeout(debounceRef.current);
      window.clearTimeout(presenceDebounceRef.current);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [enabled, getAccessToken, scheduleRefresh]);

  return status;
}
